/**
 * ORO API Transport — web (browser) implementation.
 *
 * This module owns the HTTP transport primitives (base URL resolution,
 * the generic request wrapper, and the verb helpers). It is platform-specific:
 * the browser build uses cookie-based sessions (`credentials: 'include'`) and
 * Vite env vars. A sibling `apiTransport.native.ts` provides the React Native
 * (Expo) implementation — Metro auto-prefers the `.native.ts` extension, while
 * Vite/web ignores it. Everything downstream imports these symbols via
 * `lib/api.ts` (which re-exports this module), so domain modules and the
 * `*Api` objects are transport-agnostic and work on both platforms unchanged.
 *
 * Keep the public surface of this file and `apiTransport.native.ts` identical.
 */

import { assertNoAccountMismatch, expectedUserHeaders, guardedFetch } from './accountGuard';

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

/** Production domains that all use api.oro.fit */
const PRODUCTION_DOMAINS = ['oro.fit', 'oro.run', 'oro.bike'];

/** Single source of truth for API base URL - import this, don't redefine */
export const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  // Safe host-based defaults for subdomain architecture
  (PRODUCTION_DOMAINS.includes(hostname)
    ? 'https://api.oro.fit'
    : hostname === 'dev.oro.fit'
    ? 'https://api-dev.oro.fit'
    : hostname === 'qa.oro.fit'
    ? 'https://api-qa.oro.fit'
    : '/api'); // last resort for local dev proxies

if (import.meta.env.DEV) {
  console.log('[ORO] API_BASE_URL —', API_BASE_URL);
}

/**
 * Standard API Error class matching ARCHITECTURE_RULES.md
 * Backend returns: { error: { code, message, details? } }
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  /** Server-correlation id from the error envelope (`{ error: { requestId } }`), when present. */
  readonly requestId?: string;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown>,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }
}

/**
 * Options for API requests supporting idempotency and cancellation
 * Used by mutation hooks (chat, RSVP, check-ins) for deduplication
 */
export interface ApiRequestOptions {
  /** Idempotency key for duplicate request prevention (X-Idempotency-Key header) */
  idempotencyKey?: string;
  /** AbortSignal for request cancellation */
  signal?: AbortSignal;
}

/**
 * Generic API request wrapper
 * Supports idempotency keys and abort signals for mutation deduplication
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  requestOptions?: ApiRequestOptions,
  // Session mode. Defaults to 'include' (cookie session — every existing call).
  // `apiPostPublic` passes 'omit' for a deliberately anonymous request.
  credentials: RequestCredentials = 'include'
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  // Build headers with optional idempotency key
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (requestOptions?.idempotencyKey) {
    (headers as Record<string, string>)['X-Idempotency-Key'] = requestOptions.idempotencyKey;
  }

  const response = await guardedFetch(url, {
    ...options,
    credentials,
    headers,
    signal: requestOptions?.signal,
  });

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    // Parse backend error response: { error: { code, message, details? } }
    if (contentType.includes('application/json')) {
      try {
        const body = await response.json();
        if (body.error) {
          // Backend standard error shape
          throw new ApiError(
            body.error.message || 'An error occurred',
            body.error.code || 'UNKNOWN_ERROR',
            response.status,
            body.error.details,
            body.error.requestId
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
        // JSON parse failed or unexpected format
      }
    }
    // Fallback for non-JSON or malformed responses
    throw new ApiError(
      `HTTP ${response.status}: ${response.statusText}`,
      'HTTP_ERROR',
      response.status
    );
  }

  // 204 No Content or explicit empty response
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  // Non-JSON successful response (CF error pages, etc.)
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * GET request
 */
export async function apiGet<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'GET' });
}

/**
 * POST request
 * @param options - Optional idempotency key and abort signal for mutation deduplication
 */
export async function apiPost<T>(
  path: string,
  body?: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  }, options);
}

/**
 * Public (session-free) POST — the web twin of native `apiPostPublic`. Sends NO
 * cookies (`credentials: 'omit'`), so a deliberately anonymous submission never
 * rides the caller's session. Web parity note: signed-out web already has no
 * session cookie, so this changes nothing for a guest; it exists so the shared
 * `contactApi.submitAnonMessage` resolves on BOTH platforms (native's variant is
 * the security-critical one — it must not attach a stored Bearer). #3168.
 */
export async function apiPostPublic<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(
    path,
    { method: 'POST', body: body ? JSON.stringify(body) : undefined },
    undefined,
    'omit'
  );
}

/**
 * PATCH request
 * @param options - Optional idempotency key and abort signal for mutation deduplication
 */
export async function apiPatch<T>(
  path: string,
  body?: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  }, options);
}

/**
 * PUT request
 * @param options - Optional idempotency key and abort signal for mutation deduplication
 */
export async function apiPut<T>(
  path: string,
  body?: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  }, options);
}

/**
 * DELETE request
 * @param options - Optional idempotency key and abort signal for mutation deduplication
 */
export async function apiDelete<T>(
  path: string,
  options?: ApiRequestOptions
): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' }, options);
}

/** Options for multipart uploads. Extends ApiRequestOptions with upload progress. */
export interface UploadRequestOptions extends ApiRequestOptions {
  /**
   * Upload progress callback, called with a 0..1 fraction as the request body
   * is sent. Only fires when the runtime reports computable lengths.
   */
  onProgress?: (fraction: number) => void;
}

/** Raw XHR outcome captured for envelope parsing (mirrors apiRequest's parsing inputs). */
interface UploadHttpResult {
  status: number;
  statusText: string;
  contentType: string;
  bodyText: string;
}

/** Named AbortError so mutation wrappers' silent-abort checks recognize cancels. */
function makeAbortError(): Error {
  const error = new Error('Upload aborted');
  error.name = 'AbortError';
  return error;
}

/**
 * POST a FormData body via XMLHttpRequest. XHR (not fetch) because fetch
 * exposes no upload progress. Cookie-session flavor (withCredentials); the
 * native sibling carries Bearer auth. No Content-Type header is set — XHR
 * derives the multipart boundary itself.
 */
function performUploadRequest(
  url: string,
  formData: FormData,
  headers: Record<string, string>,
  options?: UploadRequestOptions
): Promise<UploadHttpResult> {
  return new Promise((resolve, reject) => {
    if (options?.signal?.aborted) {
      reject(makeAbortError());
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true; // Cookie session, mirrors apiRequest's credentials: 'include'
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }

    const onAbortSignal = () => xhr.abort();
    options?.signal?.addEventListener('abort', onAbortSignal);
    const cleanup = () => options?.signal?.removeEventListener('abort', onAbortSignal);

    const onProgress = options?.onProgress;
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };
    }

    xhr.onload = () => {
      cleanup();
      resolve({
        status: xhr.status,
        statusText: xhr.statusText,
        contentType: xhr.getResponseHeader('content-type') ?? '',
        bodyText: xhr.responseText ?? '',
      });
    };
    xhr.onerror = () => {
      cleanup();
      reject(new ApiError('Network request failed', 'NETWORK_ERROR', 0));
    };
    xhr.onabort = () => {
      cleanup();
      reject(makeAbortError());
    };
    // No xhr.timeout is set today, but a future timeout must reject (and
    // clean up), never hang the promise (silent-failure-hunter P3, #2114).
    xhr.ontimeout = () => {
      cleanup();
      reject(new ApiError('Upload timed out', 'TIMEOUT', 0));
    };

    try {
      xhr.send(formData);
    } catch (err) {
      // RN can throw synchronously on a bad FormData descriptor — without
      // cleanup the abort listener would outlive the dead XHR.
      cleanup();
      reject(err);
    }
  });
}

/** Mirror of apiRequest's response parsing for XHR results — same envelope + status semantics. */
function parseUploadResult<T>(result: UploadHttpResult): T {
  const isJson = result.contentType.includes('application/json');
  const ok = result.status >= 200 && result.status < 300;

  if (!ok) {
    if (isJson) {
      try {
        const body = JSON.parse(result.bodyText) as {
          error?: {
            message?: string;
            code?: string;
            details?: Record<string, unknown>;
            requestId?: string;
          };
        };
        if (body.error) {
          throw new ApiError(
            body.error.message || 'An error occurred',
            body.error.code || 'UNKNOWN_ERROR',
            result.status,
            body.error.details,
            body.error.requestId
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
        // JSON parse failed or unexpected format — fall through to generic error.
      }
    }
    throw new ApiError(
      `HTTP ${result.status}: ${result.statusText}`,
      'HTTP_ERROR',
      result.status
    );
  }

  // Unlike the JSON verbs' parseResponse, a 2xx WITHOUT a JSON envelope is an
  // ERROR here: upload endpoints always return { image: ... }, and a captive
  // portal / proxy 200 must not resolve into a false success toast
  // (silent-failure-hunter P3, #2114).
  if (!isJson || result.bodyText === '') {
    throw new ApiError(
      'Upload response was not a valid envelope',
      'MALFORMED_RESPONSE',
      result.status
    );
  }
  return JSON.parse(result.bodyText) as T;
}

/**
 * POST multipart/form-data over the cookie session.
 *
 * The JSON verb helpers can't carry FormData (they force Content-Type:
 * application/json), so uploads get their own XHR path with the same envelope
 * semantics as apiRequest. Callers must NOT set Content-Type on the FormData;
 * the multipart boundary is XHR-derived.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  options?: UploadRequestOptions
): Promise<T> {
  const headers: Record<string, string> = { ...expectedUserHeaders() };
  if (options?.idempotencyKey) {
    headers['X-Idempotency-Key'] = options.idempotencyKey;
  }
  const result = await performUploadRequest(`${API_BASE_URL}${path}`, formData, headers, options);
  assertNoAccountMismatch(result.status, result.bodyText);
  return parseUploadResult<T>(result);
}
